import { Routes, Route, Navigate } from 'react-router-dom';
import { Suspense, lazy } from 'react';
import Layout from '@/components/layout';
import ROUTES from './path';
import { LoadingSpinner } from '@/components/common/loading-spinner';
import { CommonRoute, CreatorRoute } from './private-route';

const handleChunkFail = (err: any) => {
  console.warn('Lazy load failed, forcing reload');
  window.location.reload();
  throw err;
};

const withFallback = (children: React.ReactNode) => <Suspense fallback={<LoadingSpinner />}>{children}</Suspense>;

// Helper to render routes recursively
const renderRoutes = (routes: any[]) =>
  routes.map(({ children, ...route }, i) =>
    children ? (
      <Route key={i} {...route}>
        {renderRoutes(children)}
      </Route>
    ) : (
      <Route key={i} {...route} />
    )
  );

const pages = {
  Login: lazy(() => import('@/pages/authentication/login').catch(handleChunkFail)),
  AutoLogin: lazy(() => import('@/pages/authentication/auto-login').catch(handleChunkFail)),
  BeneficiarySignIn: lazy(() => import('@/pages/authentication/beneficiary-signin').catch(handleChunkFail)),
  NotFound: lazy(() => import('@/pages/not-found').catch(handleChunkFail)),
  Error: lazy(() => import('@/pages/error').catch(handleChunkFail)),
  Viewer: lazy(() => import('@/pages/viewer/viewer').catch(handleChunkFail)),
  StartFreeTrial: lazy(() => import('@/pages/authentication/start-trial').catch(handleChunkFail)),
  Welcome: lazy(() => import('@/pages/welcome').catch(handleChunkFail)),
  HomeOutlet: lazy(() => import('@/pages/home/home-outlet').catch(handleChunkFail)),
  Home: lazy(() => import('@/pages/home/home').catch(handleChunkFail)),
  HomeAddBeneficiaries: lazy(() => import('@/pages/home/home-addbeneficiaries').catch(handleChunkFail)),
  HomeLikeness: lazy(() => import('@/pages/home/home-likeness').catch(handleChunkFail)),
  HomeUpload: lazy(() => import('@/pages/home/home-upload').catch(handleChunkFail)),
  HomePersonality: lazy(() => import('@/pages/home/home-personality').catch(handleChunkFail)),
  HomeRecordVoice: lazy(() => import('@/pages/home/home-recordvoice').catch(handleChunkFail)),
  TwinCreationOutlet: lazy(() => import('@/pages/twincreation/twin-creation-outlet').catch(handleChunkFail)),
  TwinIntro: lazy(() => import('@/pages/twincreation/twin-step1-intro').catch(handleChunkFail)),
  TwinPermission: lazy(() => import('@/pages/twincreation/twin-step2b-permission').catch(handleChunkFail)),
  TwinChooseFlow: lazy(() => import('@/pages/twincreation/twin-step2-chooseFlow').catch(handleChunkFail)),
  TwinLikenessGuide: lazy(() => import('@/pages/twincreation/twin-step3a-likeness-guide').catch(handleChunkFail)),
  TwinLikenessConsent: lazy(() => import('@/pages/twincreation/twin-step3b-likeness-consent').catch(handleChunkFail)),
  TwinLikenessRecord: lazy(() => import('@/pages/twincreation/twin-step3c-likeness').catch(handleChunkFail)),
  TwinInterview: lazy(() => import('@/pages/twincreation/twin-step4-interview').catch(handleChunkFail)),
  TwinUpload: lazy(() => import('@/pages/twincreation/twin-step6-upload').catch(handleChunkFail)),
  TwinAddBeneficiaries: lazy(() => import('@/pages/twincreation/twin-step7-addbeneficiaries').catch(handleChunkFail)),
  TwinEnd: lazy(() => import('@/pages/twincreation/twin-step8-end').catch(handleChunkFail)),
  AudioFlowOutlet: lazy(() => import('@/pages/twincreation-audioFlow/twin-audioflow-outlet').catch(handleChunkFail)),
  AudioFlowGuide: lazy(() => import('@/pages/twincreation-audioFlow/step3a-guide').catch(handleChunkFail)),
  AudioFlowLikeness: lazy(() =>
    import('@/pages/twincreation-audioFlow/step3b-audioflow-likeness').catch(handleChunkFail)
  ),
};

// Define your routes as config objects
const publicRoutes = [
  { path: ROUTES.login, element: withFallback(<pages.Login />) },
  {
    path: ROUTES.beneficiarySignIn,
    element: withFallback(
      <Layout>
        <pages.BeneficiarySignIn />
      </Layout>
    ),
  },
  {
    path: ROUTES.autoLogin,
    element: withFallback(
      <Layout>
        <pages.AutoLogin />
      </Layout>
    ),
  },
];

const creatorRoutes = [
  { path: ROUTES.welcome, element: withFallback(<pages.Welcome />) },
  {
    path: ROUTES.home_Outlet,
    element: <pages.HomeOutlet />,
    children: [
      { index: true, element: withFallback(<pages.Home />) },
      { path: ROUTES.homeAddBeneficiaries, element: withFallback(<pages.HomeAddBeneficiaries />) },
      { path: ROUTES.homeLikeness, element: withFallback(<pages.HomeLikeness />) },
      { path: ROUTES.HomeUpload, element: withFallback(<pages.HomeUpload />) },
      { path: ROUTES.homeRecordVoice, element: withFallback(<pages.HomeRecordVoice />) },
      { path: ROUTES.homePersonality, element: withFallback(<pages.HomePersonality />) },
    ],
  },
  {
    path: ROUTES.twinCreation_Outlet,
    element: <pages.TwinCreationOutlet />,
    children: [
      { path: ROUTES.twinIntro, element: withFallback(<pages.TwinIntro />) },
      { path: ROUTES.twinPermission, element: withFallback(<pages.TwinPermission />) },
      { path: ROUTES.twinChooseFlow, element: withFallback(<pages.TwinChooseFlow />) },
      { path: ROUTES.twinLikenessGuide, element: withFallback(<pages.TwinLikenessGuide />) },
      { path: ROUTES.twinLikenessConsent, element: withFallback(<pages.TwinLikenessConsent />) },
      { path: ROUTES.twinLikeness, element: withFallback(<pages.TwinLikenessRecord />) },
      { path: ROUTES.twinInterview, element: withFallback(<pages.TwinInterview />) },
      { path: ROUTES.twinUpload, element: withFallback(<pages.TwinUpload />) },
      { path: ROUTES.twinAddBeneficiaries, element: withFallback(<pages.TwinAddBeneficiaries />) },
      { path: ROUTES.twinEnd, element: withFallback(<pages.TwinEnd />) },
    ],
  },
  {
    path: ROUTES.audioFlow_Outlet,
    element: <pages.AudioFlowOutlet />,
    children: [
      { path: ROUTES.audioFlow_guide, element: withFallback(<pages.AudioFlowGuide />) },
      { path: ROUTES.audioFlowLikeness, element: withFallback(<pages.AudioFlowLikeness />) },
    ],
  },
];

const AppRoutes = () => (
  <Routes>
    <Route index element={<Navigate to={ROUTES.login} replace />} />
    {renderRoutes(publicRoutes)}
    <Route element={<Layout />}>
      <Route element={<CreatorRoute />}>{renderRoutes(creatorRoutes)}</Route>
      <Route element={<CommonRoute />}>
        <Route path={ROUTES.viewer} element={<pages.Viewer />} />
        <Route path={ROUTES.startFreeTrial} element={<pages.StartFreeTrial />} />
      </Route>
      <Route path="*" element={<pages.NotFound />} />
      <Route path={ROUTES.error} element={<pages.Error />} />
    </Route>
  </Routes>
);

export default AppRoutes;
